// client/src/services/eventsApi.js
// API wrapper for fetching all event types (events, auctions, raffles, social events)
// This provides a consistent interface for the EventsList and ScheduledEventsList components

import axios from "axios";

// Resolve base URL once, trimming any trailing slash
const BASE_URL = (
  process.env.REACT_APP_BASE_URL ||
  window.location.origin ||
  ""
).replace(/\/+$/, "");

/**
 * Normalizes axios errors so components can display user-friendly messages
 * but you still get full details in the console.
 */
function normalizeError(error) {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    "Unknown error";
  const status = error?.response?.status;

  const err = new Error(message);
  err.status = status;
  err.raw = error;
  return err;
}

/**
 * Fetch all events (including auctions, raffles, and social event types) for a tenant.
 *
 * Returns the backend shape:
 * {
 *   success: true,
 *   events: [
 *     {
 *       id: number,
 *       type: string, // 'event', 'auction', 'raffle', 'challenge', 'contest', etc.
 *       eventId: number, // parent event ID
 *       eventItemId: number | null, // item ID if applicable
 *       name: string,
 *       description: string | null,
 *       startDate: datetime | null,
 *       endDate: datetime | null,
 *       status: string,
 *       eventName: string | null, // parent event name
 *       itemName: string | null, // event item name if applicable
 *       files: [{ id, path, type }], // media files
 *       metadata: object // type-specific fields
 *       createdOn: datetime
 *     }, ...
 *   ],
 *   pagination: { total, limit, offset, hasMore }
 * }
 *
 * @param {number|string} tenantId - The tenant ID
 * @param {Object} options - Query options
 * @param {number} [options.limit=50] - Items per page (default: 50, max: 100)
 * @param {number} [options.offset=0] - Pagination offset (default: 0)
 * @param {string|null} [options.type=null] - Filter by specific event type (optional)
 * @param {string|null} [options.status=null] - Filter by status (optional)
 * @returns {Promise<Object>} Response with events and pagination data
 */
export async function fetchAllEvents(
  tenantId,
  { limit = 50, offset = 0, type = null, status = null } = {}
) {
  try {
    const params = { limit, offset };
    if (type) params.type = type;
    if (status) params.status = status;

    const res = await axios.get(
      `${BASE_URL}/api/events/${tenantId}/all-events`,
      {
        params,
        withCredentials: true,
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching all events", error);
    throw normalizeError(error);
  }
}

/**
 * Fetch ONLY scheduled / upcoming events for a tenant.
 *
 * Backend route: GET /api/events/:tenantId/scheduled-events
 * Expected response shape:
 * {
 *   success: true,
 *   events: [...],
 *   pagination: { total, limit, offset, hasMore }
 * }
 *
 * @param {number|string} tenantId - The tenant ID or slug
 * @param {Object} options - Query options
 * @param {number} [options.limit=50] - Items per page
 * @param {number} [options.offset=0] - Pagination offset
 * @param {string|null} [options.type=null] - Optional filter for a specific event type
 * @param {string|null} [options.status=null] - Optional status filter (e.g., 'NEW', 'ACTIVE')
 * @returns {Promise<Object>} Response with events and pagination data
 */
export async function fetchScheduledEvents(
  tenantId,
  { limit = 50, offset = 0, type = null, status = null } = {}
) {
  try {
    const params = { limit, offset };
    if (type) params.type = type;
    if (status) params.status = status;

    const res = await axios.get(
      `${BASE_URL}/api/events/${tenantId}/scheduled-events`,
      {
        params,
        withCredentials: true,
      }
    );

    // Mirror fetchAllEvents behavior: return the backend JSON as-is
    return res.data;
  } catch (error) {
    console.error("Error fetching scheduled events", error);
    throw normalizeError(error);
  }
}

/**
 * Helper function to format event type display names
 * @param {string} type - The event type from the backend
 * @returns {string} Display-friendly type name
 */
export function formatEventType(type) {
  const typeMap = {
    event: "Event",
    auction: "Auction",
    raffle: "Raffle",
    challenge: "Challenge",
    contest: "Contest",
    "poll-quiz": "Poll/Quiz",
    "ugc-campaign": "UGC Campaign",
    ama: "AMA",
    "collab-takeover": "Collaboration",
    "themed-day": "Themed Day",
  };
  return typeMap[type] || type;
}

/**
 * Helper function to get badge color for event status
 * @param {string} status - The event status
 * @returns {string} Bootstrap badge color class
 */
export function getStatusBadgeClass(status) {
  const statusLower = String(status || "").toLowerCase();

  if (statusLower === "active" || statusLower === "live") return "bg-success";
  if (statusLower === "upcoming" || statusLower === "new") return "bg-primary";
  if (statusLower === "completed" || statusLower === "complete")
    return "bg-secondary";
  if (statusLower === "cancelled" || statusLower === "canceled")
    return "bg-danger";
  if (statusLower === "draft") return "bg-warning";

  return "bg-secondary";
}

/**
 * Helper function to get icon for event type (using Font Awesome classes)
 * @param {string} type - The event type
 * @returns {string} Font Awesome icon class
 */
export function getEventTypeIcon(type) {
  const iconMap = {
    event: "fa-calendar-alt",
    auction: "fa-gavel",
    raffle: "fa-ticket-alt",
    challenge: "fa-trophy",
    contest: "fa-award",
    "poll-quiz": "fa-poll",
    "ugc-campaign": "fa-camera",
    ama: "fa-user-friends",
    "collab-takeover": "fa-handshake",
    "themed-day": "fa-hashtag",
  };
  return iconMap[type] || "fa-calendar";
}

// ---------------------------------------------------------------------------
// On-Screen 2.0 — event lifecycle
// ---------------------------------------------------------------------------

/** Ordered lifecycle stages (mirrors server/services/eventLifecycle.js). */
export const EVENT_LIFECYCLE = [
  "draft",
  "planning",
  "promoting",
  "registration_open",
  "registration_closed",
  "in_progress",
  "completed",
  "archived",
];

export const EVENT_LIFECYCLE_LABELS = {
  draft: "Draft",
  planning: "Planning",
  promoting: "Promoting",
  registration_open: "Registration open",
  registration_closed: "Registration closed",
  in_progress: "In progress",
  completed: "Completed",
  archived: "Archived",
};

/**
 * Advance an event through its lifecycle.
 * @param {number|string} tenantId
 * @param {number|string} eventId
 * @param {string} status - target lifecycle status
 * @returns {Promise<{success:boolean,status:string,from:string,allowed?:string[]}>}
 */
export async function changeEventStatus(tenantId, eventId, status) {
  try {
    const res = await axios.patch(
      `${BASE_URL}/api/events/${tenantId}/events/${eventId}/status`,
      { status },
      { withCredentials: true }
    );
    return res.data;
  } catch (error) {
    console.error("Error changing event status", error);
    throw normalizeError(error);
  }
}
